import { ADMIN_API_URL, ADMIN_APP_ID } from './apiConfig';
import { getStoredToken } from './authApi';

const AUTH_PREFIX = `${ADMIN_API_URL}`;

async function request(path, options = {}) {
  const token = getStoredToken();
  const isFormData = options.body instanceof FormData;
  const res = await fetch(`${AUTH_PREFIX}${path}`, {
    ...options,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'X-App-Id': ADMIN_APP_ID,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json?.error ?? json?.message ?? `Request failed: ${res.status}`);
  }
  return json?.data ?? json;
}

function toArray(value, keys = []) {
  if (Array.isArray(value)) return value;
  for (const key of [...keys, 'data', 'result', 'rows', 'items']) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  for (const key of keys) {
    if (Array.isArray(value?.data?.[key])) return value.data[key];
    if (Array.isArray(value?.result?.[key])) return value.result[key];
  }
  return [];
}

function normalizeUser(user) {
  const userId = user.user_id ?? user.userId ?? user.UserId;
  const roleId = user.user_type_id ?? user.roleId ?? user.role_id ?? user.RoleId;
  const firstName = user.first_name ?? user.firstName ?? '';
  const lastName = user.last_name ?? user.lastName ?? '';
  return {
    ...user,
    user_id: userId,
    username: user.username ?? user.userName ?? user.user_name ?? `${firstName}${lastName}`.trim(),
    first_name: firstName,
    last_name: lastName,
    email: user.email,
    mobile: user.mobile ?? user.phoneNumber ?? user.phone_number ?? user.mobile_number,
    user_type_id: roleId,
    role_id: roleId,
    profile_image: user.profile_image ?? user.photoLocation ?? user.photo_location,
    status: normalizeStatus(user.status ?? user.active_status),
  };
}

function normalizeRole(role) {
  const roleId = role.user_type_id ?? role.role_id ?? role.roleId ?? role.id;
  return {
    ...role,
    user_type_id: roleId,
    user_type_name: role.user_type_name ?? role.role_name ?? role.roleName ?? role.name,
    description: role.description ?? role.role_description ?? role.roleDescription ?? '',
    status: normalizeStatus(role.status ?? role.active_status ?? 'ACTIVE'),
  };
}

function normalizeMenu(menu) {
  const menuId = menu.menu_id ?? menu.menuId ?? menu.legacyMenuId ?? menu.id;
  const parentMenuId = menu.parent_menu_id ?? menu.parentMenuId;
  const menuName = menu.menu_name ?? menu.menuName ?? menu.name ?? menu.label;
  return {
    ...menu,
    menu_id: menuId,
    menu_name: menuName,
    menu_key: menu.menu_key ?? menu.menuKey ?? menu.key,
    api_url: menu.api_url ?? menu.apiUrl ?? menu.route ?? '',
    menu_type: menu.menu_type ?? menu.menuType ?? menu.placement ?? 'MENU',
    parent_menu_id: parentMenuId || null,
    component_name: menu.component_name ?? menu.componentName ?? '',
    display_order: menu.display_order ?? menu.displayOrder ?? menuId ?? 0,
    status: normalizeStatus(menu.status),
  };
}

function normalizeStatus(value) {
  if (value === true || value === 1 || value === '1') return 'ACTIVE';
  const status = String(value ?? 'ACTIVE').trim().toUpperCase();
  return status === 'ACTIVE' || status === 'ENABLED' ? 'ACTIVE' : 'INACTIVE';
}

function toUserPayload(user) {
  return {
    ...user,
    user_name: user.username ?? user.user_name,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    user_pwd: user.password ?? user.user_pwd,
    phone_number: user.mobile ?? user.phone_number,
    mobile_number: user.mobile ?? user.mobile_number,
    role_id: user.user_type_id ?? user.role_id,
    status: user.status,
  };
}

function formDataJson(payload) {
  const formData = new FormData();
  formData.append('json', JSON.stringify(payload));
  return formData;
}

function toMenuPayload(menu) {
  return {
    ...menu,
    menuName: menu.menu_name,
    name: menu.menu_name,
    menuKey: menu.menu_key,
    key: menu.menu_key,
    parentMenuId: Number(menu.parent_menu_id ?? 0),
    placement: menu.menu_type ?? 'MENU',
    icon: menu.icon,
    status: menu.status,
  };
}

function normalizePermissionGroups(groups) {
  const source = Array.isArray(groups)
    ? groups
    : Object.entries(groups ?? {}).map(([menuName, group]) => ({ ...group, menu_name: menuName }));

  return source.map((group) => {
    const menuId = group.menu_id ?? group.menuId;
    const menuName = group.menu_name ?? group.menuName ?? group.name ?? String(menuId ?? '');
    const permissions = Object.entries(group.permissions ?? {}).reduce((acc, [key, permission]) => {
      acc[key] = {
        ...permission,
        permission_id: permission?.permission_id ?? permission?.permissionId ?? permission?.id,
        permission_key: permission?.permission_key ?? permission?.permissionKey ?? key,
        value: permission?.value ?? permission?.access_value ?? permission,
      };
      return acc;
    }, {});
    return {
      ...group,
      menu_id: menuId,
      menu_name: menuName,
      menu_key: group.menu_key ?? group.menuKey ?? menuName,
      permissions,
    };
  });
}

function toPermissionUpdatePayload(roleId, rows) {
  const menus = rows.reduce((acc, row) => {
    const menuName = row.menu_name ?? row.menuName ?? row.menu_key ?? String(row.menu_id);
    const permissionKey = row.permission_key ?? row.permissionKey;
    if (!permissionKey) return acc;
    if (!acc[menuName]) {
      acc[menuName] = {
        menuId: row.menu_id ?? row.menuId,
        permissions: {},
      };
    }
    acc[menuName].permissions[permissionKey] = {
      id: row.permission_id ?? row.permissionId,
      value: String(Number(row.access_value ?? row.accessValue) === 1 ? 1 : 0),
    };
    return acc;
  }, {});

  return { roleId, menus };
}

export function listUsers() {
  return request('/get-all-users?limit=500&offset=0').then((data) => (
    toArray(data, ['users']).map(normalizeUser)
  ));
}

export function createUser(user) {
  return request('/create-user', {
    method: 'POST',
    body: formDataJson(toUserPayload(user)),
  });
}

export function updateUser(userId, user) {
  return request(`/update-user?userId=${encodeURIComponent(userId)}`, {
    method: 'PUT',
    body: formDataJson(toUserPayload(user)),
  });
}

export function deleteUser(userId) {
  return request(`/delete-user?userId=${encodeURIComponent(userId)}`, {
    method: 'PUT',
  });
}

export function listUserTypes() {
  return request('/roles-list').then((data) => toArray(data).map(normalizeRole));
}

export function listMenus() {
  return request('/admin/menu-modules').then((data) => toArray(data).map(normalizeMenu));
}

export function createMenu(menu) {
  return request('/admin/menu-modules', {
    method: 'POST',
    body: JSON.stringify(toMenuPayload(menu)),
  });
}

export function updateMenu(menuId, menu) {
  return request(`/admin/menu-modules/${menuId}`, {
    method: 'PUT',
    body: JSON.stringify(toMenuPayload(menu)),
  });
}

export function deleteMenu(menuId) {
  return request(`/admin/menu-modules/${menuId}`, {
    method: 'DELETE',
  });
}

export function listMenuPermissions() {
  return request('/admin/menu-actions');
}

export function getRoleMenuPermissions(userTypeId) {
  return request(`/get-role-details?roleid=${encodeURIComponent(userTypeId)}`)
    .then(normalizePermissionGroups);
}

export function saveRoleMenuPermissions(userTypeId, permissions) {
  return request('/edit-role-details', {
    method: 'PUT',
    body: JSON.stringify(toPermissionUpdatePayload(userTypeId, permissions)),
  });
}

export function getUserMenuPermissions(userId) {
  return request(`/get-role-details?userId=${encodeURIComponent(userId)}`)
    .then(normalizePermissionGroups);
}

export function saveUserMenuPermissions(userId, permissions) {
  return request('/edit-role-details', {
    method: 'PUT',
    body: JSON.stringify({ ...toPermissionUpdatePayload(null, permissions), userId }),
  });
}
